import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
} from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import ChromeReaderModeIcon from '@mui/icons-material/ChromeReaderMode';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import { Selector } from 'components/Form/Selectors/Selector';
import { MultiSelector } from 'components/Form/Selectors/MultiSelector';
import AssetHandler from 'handlers/asset.handler';
import { useEffect, useState } from 'react';
import { modalInputFieldStyle } from 'styles/global.css';
import { createOrUpdateAssetRequestBodySchema } from 'utils/schemas/asset.schema';
import { validateYupSchema } from 'utils/validators/schema.validator';
import { SWTextField } from 'components/Form/Input/SWTextField';
import type { ICreateAssetPayload } from 'types/payloads/asset.payload.types';
import { ASSET_TYPES } from 'utils/enums';
import type {
  ASSET_TYPES_ENUM,
  Asset,
  assetNotesInterface,
} from 'types/asset.types';
import {
  handleSelectOperatorSingle,
  handleSelectOperatorAll,
  handleOperationalStatus,
  handleAssetTPMS,
} from 'views/Settings/ExternalUsers/AssetView/helpers/common';
import { useAppSelector } from 'store/hook';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import { TirePatternSelector } from './TirePatternSelector';
import { ThemePalette } from 'mui.theme';
import { getDynamicAssetTireInfo } from 'views/TirePressure/util';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  /**
   * Title of the Modal
   */
  title: string;
  /**
   * Boolean Value to determine if modal is displpayed or not
   */
  open: boolean;
  /**
   * Asset Data modal is to render
   */
  assetData: Asset;
  /**
   * Function to call to close modal
   */
  handleAbort: () => void;
  enableDarkTheme: boolean;
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  makeModelDetails: MakeModelDetails[];
  customerId: string;
}

export const UpdateAssetModal = ({
  title,
  handleAbort,
  open,
  assetData,
  enableDarkTheme,
  tirePatterns,
  makeModelDetails,
  customerId,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const assetHandler = new AssetHandler();
  const [isValidForm, setFormValidity] = useState(false);
  const [asset, setAsset] = useState<ICreateAssetPayload>({
    assetVin: assetData.assetVin,
    assetType: assetData.assetType,
    make: assetData.make,
    model: assetData.model,
    bumperNumber: assetData.bumperNumber,
    engineHoursOffset: assetData.engineHoursOffset,
    odometerOffset: assetData.odometerOffset,
    fuelSource: assetData.fuelSource,
    operators: assetData.operators || [],
    isActive: assetData.isActive,
    externalId: assetData.externalId,
    assetNotes: [],
    tpmsEnabled: assetData.tpmsEnabled,
  });

  const assetTireDetails = getDynamicAssetTireInfo(
    makeModelDetails,
    tirePatterns.tirePatternPositions,
    assetData.make,
    assetData.model,
    customerId
  );
  const [assetTirePattern, setAssetTirePattern] = useState<string>(
    assetData.patternName ?? assetTireDetails.patternName
  );

  const [showNotes, setShowNotes] = useState(true);

  const { operators } = useAppSelector((state) => state.operatorReducer);
  const transformedOperators: Array<{ id: string; display: string }> = operators
    .filter((operator) => operator.isActive) // Filter only active operators
    .map((operator) => {
      return {
        id: operator.operatorId,
        display: operator.operatorName,
      };
    });

  // Validate form as user fills out fields
  useEffect(() => {
    const validateForm = async () =>
      await validateYupSchema(asset, createOrUpdateAssetRequestBodySchema);

    void validateForm().then((isValid) => {
      setFormValidity(isValid);
    });
  }, [asset]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setAsset({
      ...asset,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    await assetHandler.updateAsset(
      assetData.assetId,
      { ...asset, patternName: assetTirePattern },
      assetData?.tpmsEnabled ?? false
    );

    handleAbort();
  };
  /**
   * This is the form that will be rendered in the modal
   *
   * Asset VIN/ID
   */
  return (
    <InteractiveModal
      initialState={open}
      SuccessButtonProps={{
        disabled: !isValidForm,
        onClick: handleSubmit,
        label: 'SAVE EDITS',
      }}
      handleAbort={handleAbort}
      title={title}
      confirmCancellation
      enableDarkTheme={enableDarkTheme}
    >
      {/* Asset VIN/ID TextField */}
      <SWTextField
        key="assetVin"
        name="assetVin"
        label={t('asset_vin_id')}
        placeholder={t('enter_vin_id_number')}
        TextFieldProps={{
          name: 'assetVin',
          value: asset.assetVin,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Asset Type selector drop down single */}
      <Box key="assetType">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        >
          {t('asset_type_2')}
        </Typography>
        <Selector
          value={asset.assetType}
          onChange={(event) => {
            const selectedValue = event.target.value as ASSET_TYPES_ENUM;
            setAsset({
              ...asset,
              assetType: selectedValue,
            });
          }}
          selectorOptions={ASSET_TYPES}
          enableDarkTheme={enableDarkTheme}
        />
      </Box>

      <SWTextField
        key="make"
        name="make"
        label={t('make')}
        placeholder={t('enter_asset_make')}
        TextFieldProps={{
          name: 'make',
          value: asset.make,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
          /*
           * We're disabling make/model updates b/c
           * they've now become key categories for Asset
           * grouping in tags and tag onboarding
           *
           * TODO, we might look into squashing these fields into one
           * to make development easier
           */
          disabled: true,
        }}
        enableDarkTheme={enableDarkTheme}
      />

      <SWTextField
        key="model"
        name="model"
        label={t('model_2')}
        placeholder={t('enter_asset_model')}
        TextFieldProps={{
          name: 'model',
          value: asset.model,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
          /*
           * We're disabling make/model updates b/c
           * they've now become key categories for Asset
           * grouping in tags and tag onboarding
           *
           * TODO, we might look into squashing these fields into one
           * to make development easier
           */
          disabled: true,
        }}
        enableDarkTheme={enableDarkTheme}
      />

      <SWTextField
        key="bumperNumber"
        name="bumperNumber"
        label={t('bumper_id_10')}
        placeholder={t('enter_bumper_id')}
        TextFieldProps={{
          name: 'bumperNumber',
          value: asset.bumperNumber,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      <SWTextField
        key="odometerOffset"
        name="odometerOffset"
        label={t('odometer_reading_offset_km')}
        placeholder={t('enter_odometer_reading_offset')}
        TextFieldProps={{
          name: 'odometerOffset',
          value: asset.odometerOffset,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      <SWTextField
        key="engineHoursOffset"
        name="engineHoursOffset"
        label={t('engine_hours_offset')}
        placeholder={t('enter_engine_hours_offset')}
        TextFieldProps={{
          name: 'engineHoursOffset',
          value: asset.engineHoursOffset,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* External ID TextField */}
      <SWTextField
        key="externalId"
        name="externalId"
        label={t('external_id')}
        placeholder={t('enter_customer_identified_id')}
        TextFieldProps={{
          name: 'externalId',
          value: asset.externalId,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Fuel Source TextField */}
      <SWTextField
        key="fuelSource"
        name="fuelSource"
        label={t('fuel_source_2')}
        TextFieldProps={{
          name: 'fuelSource',
          value: asset.fuelSource,
          onChange: handleChange,
          InputProps: {
            sx: modalInputFieldStyle,
          },
          disabled: true,
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Operators multi selector */}
      <Box key="operators">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        >
          {t('operator_s')}
        </Typography>
        <MultiSelector
          isSelectionEmpty={true}
          messageIfSelectionEmpty="No operators available"
          value={asset.operators}
          renderValue={(selected) => {
            return `${selected?.length || 0} Operators(s) Selected`;
          }}
          handleSelectOne={(event) => {
            handleSelectOperatorSingle(event, asset, setAsset);
          }}
          handleSelectAll={() => {
            handleSelectOperatorAll(transformedOperators, asset, setAsset);
          }}
          /*
        This stubbed response be replaced with real customer accounts
        */
          selectorOptions={transformedOperators}
        />
      </Box>

      {/* Operational Status Check Box */}
      <Box key="operationalStatus">
        <FormControlLabel
          control={
            <Checkbox
              sx={{
                color: enableDarkTheme
                  ? palette.primary
                  : ThemePalette.typography.primary,
                '&.Mui-checked': {
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                },
              }}
            />
          }
          checked={!asset.isActive}
          label={t('asset_is_currently_out_of_service')}
          onChange={() => {
            handleOperationalStatus(asset, setAsset);
          }}
          sx={{
            pt: 2,
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              sx={{
                color: enableDarkTheme
                  ? palette.primary
                  : ThemePalette.typography.primary,
                '&.Mui-checked': {
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                },
              }}
            />
          }
          checked={asset.tpmsEnabled}
          label={t('enable_tpms')}
          onChange={() => {
            handleAssetTPMS(asset, setAsset);
          }}
          sx={{
            pt: 2,
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        />
      </Box>
      <>
        {asset.tpmsEnabled && (
          <Box>
            <Typography
              sx={{
                color: enableDarkTheme
                  ? ThemePalette.typography.wheat
                  : ThemePalette.typography.black,
                marginBottom: '5px',
              }}
            >
              {t('tire_pattern')}
            </Typography>
            <TirePatternSelector
              enableDarkTheme={enableDarkTheme}
              value={assetTirePattern}
              setValue={setAssetTirePattern}
            />
          </Box>
        )}
      </>

      {/* Fuel Source TextField */}
      <div style={{ width: '100%', marginTop: '1px' }}>
        <div>
          {assetData.assetNotes?.length > 0 ? (
            <Button
              variant="outlined"
              startIcon={showNotes ? <CreateIcon /> : <ChromeReaderModeIcon />}
              aria-label="addNotes"
              onClick={() => {
                setShowNotes(!showNotes);
              }}
              sx={{
                border: '1px solid #2F445E',
                color: palette.textPrimary,
                textTransform: 'none',
              }}
            >
              <span style={{ fontSize: '12px' }}>
                {showNotes ? 'Add Notes' : 'Show Notes'}
              </span>
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={showNotes ? <CreateIcon /> : <ChromeReaderModeIcon />}
              aria-label="addNotes"
              onClick={() => {
                setShowNotes(!showNotes);
              }}
              sx={{
                border: '1px solid #2F445E',
                color: palette.textPrimary,
                textTransform: 'none',
              }}
            >
              <span style={{ fontSize: '12px' }}>
                {showNotes ? 'Add Notes' : 'Show Notes'}
              </span>
            </Button>
          )}
          <span
            style={{
              paddingLeft: '20px',
              fontWeight: 'bold',
              fontSize: '14px',
              color: enableDarkTheme
                ? ThemePalette.typography.wheat
                : ThemePalette.typography.black,
            }}
          >
            {t('historical_data_for_asset')}
          </span>
        </div>
        <div
          style={{
            width: '100%',
            marginTop: '10px',
            maxHeight: '200px',
            overflow: 'scroll',
          }}
          key={'assetNotes'}
        >
          {showNotes && assetData.assetNotes?.length > 0 ? (
            <Box
              sx={{
                backgroundColor: enableDarkTheme
                  ? 'rgba(47, 68, 94, 0.3)'
                  : 'rgba(240, 245, 250, 0.6)',
                border: `1px solid ${
                  enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(47, 68, 94, 0.15)'
                }`,
                borderRadius: '4px',
                padding: '16px',
                boxShadow: enableDarkTheme
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              {assetData.assetNotes?.map(
                (item: assetNotesInterface, index: number) => {
                  return (
                    <Box
                      key={assetData?.assetNotes?.indexOf(item)}
                      sx={{
                        marginBottom:
                          index < assetData.assetNotes.length - 1 ? '16px' : 0,
                        paddingBottom:
                          index < assetData.assetNotes.length - 1 ? '16px' : 0,
                        borderBottom:
                          index < assetData.assetNotes.length - 1
                            ? `1px solid ${
                                enableDarkTheme
                                  ? 'rgba(255, 255, 255, 0.1)'
                                  : 'rgba(47, 68, 94, 0.15)'
                              }`
                            : 'none',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '16px',
                        }}
                      >
                        <Typography
                          sx={{
                            color: enableDarkTheme
                              ? ThemePalette.typography.wheat
                              : ThemePalette.typography.black,
                            fontSize: '14px',
                            lineHeight: '1.2',
                            wordBreak: 'break-word',
                            flex: 1,
                          }}
                        >
                          {item.values}
                        </Typography>
                        <Typography
                          sx={{
                            color: enableDarkTheme
                              ? 'rgba(255, 255, 255, 0.6)'
                              : 'rgba(47, 68, 94, 0.6)',
                            fontSize: '12px',
                            fontStyle: 'italic',
                            lineHeight: '1.2',
                            whiteSpace: 'nowrap',
                            '&::before': {
                              content: '"—"',
                              marginRight: '4px',
                            },
                          }}
                        >
                          {convertUtcToDisplayTime(
                            item.date_added,
                            preferredTimeDisplayMode,
                            timezone,
                            DATE_TIME_FORMAT
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }
              )}
            </Box>
          ) : showNotes ? null : (
            <SWTextField
              key="assetNotes"
              name="assetNotes"
              placeholder={t('add_new_note_here')}
              TextFieldProps={{
                name: 'assetNotes',
                onChange: handleChange,
                multiline: true,
                minRows: 2,
                fullWidth: true,
                InputProps: {
                  sx: {
                    WebkitBoxShadow: '0 0 0 1000px white inset',
                  },
                },
              }}
              enableDarkTheme={enableDarkTheme}
            />
          )}
        </div>
      </div>
    </InteractiveModal>
  );
};
